import type { ChatMessage } from "./chatApi";
import type { PendingPhotoMessage, PendingVoiceMessage } from "./ChatThreadPage";

const DEFAULT_SCROLL_BOTTOM_THRESHOLD_PX = 96;
const TEXT_MESSAGE_ESTIMATE_PX = 88;
const ATTACHMENT_MESSAGE_ESTIMATE_PX = 156;
const EDITING_MESSAGE_ESTIMATE_PX = 164;
const REFERENCE_PREVIEW_ESTIMATE_PX = 44;
const SELECTED_ACTION_MENU_ESTIMATE_PX = 54;
const PENDING_PHOTO_ESTIMATE_PX = 248;
const PENDING_VOICE_ESTIMATE_PX = 196;

export type VirtualTimelineItem =
  | { type: "message"; key: string; message: ChatMessage }
  | { type: "pending-photo"; key: string; pendingPhoto: PendingPhotoMessage }
  | { type: "pending-voice"; key: string; pendingVoice: PendingVoiceMessage };

export interface BuildVirtualTimelineItemsInput {
  messages: ChatMessage[];
  pendingPhotos: PendingPhotoMessage[];
  pendingVoices: PendingVoiceMessage[];
}

export interface TimelineScrollBox {
  scrollHeight: number;
  scrollTop: number;
  clientHeight: number;
}

export function buildVirtualTimelineItems({
  messages,
  pendingPhotos,
  pendingVoices,
}: BuildVirtualTimelineItemsInput): VirtualTimelineItem[] {
  return [
    ...messages.map((message): VirtualTimelineItem => ({
      type: "message",
      key: `message:${message.message_id}`,
      message,
    })),
    ...pendingPhotos.map((pendingPhoto): VirtualTimelineItem => ({
      type: "pending-photo",
      key: `pending-photo:${pendingPhoto.local_id}`,
      pendingPhoto,
    })),
    ...pendingVoices.map((pendingVoice): VirtualTimelineItem => ({
      type: "pending-voice",
      key: `pending-voice:${pendingVoice.local_id}`,
      pendingVoice,
    })),
  ];
}

export function resolveTimelineMessageIndex(items: VirtualTimelineItem[], messageID: string): number | null {
  const normalizedMessageID = messageID.trim();
  if (normalizedMessageID === "") {
    return null;
  }

  const index = items.findIndex((item) => item.type === "message" && item.message.message_id === normalizedMessageID);
  return index === -1 ? null : index;
}

export function estimateTimelineItemHeight(
  item: VirtualTimelineItem | undefined,
  editingMessageID?: string | null,
  selectedActionMenuMessageID?: string | null,
): number {
  if (item === undefined) {
    return TEXT_MESSAGE_ESTIMATE_PX;
  }

  switch (item.type) {
    case "pending-photo":
      return PENDING_PHOTO_ESTIMATE_PX;
    case "pending-voice":
      return PENDING_VOICE_ESTIMATE_PX;
    case "message": {
      const referencePreviewCount =
        (item.message.reply_to === undefined ? 0 : 1) +
        (item.message.forwarded_from === undefined ? 0 : 1);
      const referenceHeight = referencePreviewCount * REFERENCE_PREVIEW_ESTIMATE_PX;
      const selectedMenuHeight =
        selectedActionMenuMessageID !== undefined &&
        selectedActionMenuMessageID !== null &&
        item.message.message_id === selectedActionMenuMessageID
          ? SELECTED_ACTION_MENU_ESTIMATE_PX
          : 0;
      if (editingMessageID !== undefined && editingMessageID !== null && item.message.message_id === editingMessageID) {
        return EDITING_MESSAGE_ESTIMATE_PX + referenceHeight + selectedMenuHeight;
      }
      const baseHeight = (item.message.attachments?.length ?? 0) > 0
        ? ATTACHMENT_MESSAGE_ESTIMATE_PX
        : TEXT_MESSAGE_ESTIMATE_PX;
      return baseHeight + referenceHeight + selectedMenuHeight;
    }
    default:
      return TEXT_MESSAGE_ESTIMATE_PX;
  }
}

export function isNearScrollBottom(
  element: TimelineScrollBox,
  thresholdPx = DEFAULT_SCROLL_BOTTOM_THRESHOLD_PX,
): boolean {
  const threshold = Number.isFinite(thresholdPx) && thresholdPx >= 0 ? thresholdPx : DEFAULT_SCROLL_BOTTOM_THRESHOLD_PX;
  const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
  return distanceFromBottom <= threshold;
}

export function shouldFollowTimelineUpdate(
  element: TimelineScrollBox | null | undefined,
  thresholdPx = DEFAULT_SCROLL_BOTTOM_THRESHOLD_PX,
): boolean {
  if (element === null || element === undefined) {
    return true;
  }
  return isNearScrollBottom(element, thresholdPx);
}

export function shouldFollowTimelineUpdateWhileComposing({
  element,
  composerFocused,
  composerDraftActive,
  thresholdPx = DEFAULT_SCROLL_BOTTOM_THRESHOLD_PX,
}: {
  element: TimelineScrollBox | null | undefined;
  composerFocused: boolean;
  composerDraftActive: boolean;
  thresholdPx?: number;
}): boolean {
  if (shouldFollowTimelineUpdate(element, thresholdPx)) {
    return true;
  }
  return composerFocused && composerDraftActive;
}
